import React, { useState, useEffect } from 'react';
import { Key, Loader2 } from 'lucide-react';
import MainService from '../../services/MainService';

function isChangePasswordSuccess(response) {
  if (response === true || response === 'true' || response === 'Success') return true;
  if (response && typeof response === 'object') {
    if (response.data === true || response.Data === true) return true;
    if (response.success === true || response.Success === true) return true;
  }
  return false;
}

const ChangePassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorPass, setErrorPass] = useState(false);
  const [errorCpass, setErrorCpass] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!success) return undefined;
    const t = setTimeout(() => setSuccess(''), 4000);
    return () => clearTimeout(t);
  }, [success]);

  const resetValidation = () => {
    setErrorPass(false);
    setErrorCpass(false);
    setSubmitError('');
  };

  const validateForm = () => {
    resetValidation();
    setSuccess('');
    if (!password.trim()) {
      setErrorPass(true);
      return false;
    }
    if (!confirmPassword.trim()) {
      setErrorCpass(true);
      return false;
    }
    if (password !== confirmPassword) {
      setErrorCpass(true);
      return false;
    }
    return true;
  };

  const openConfirmModal = () => {
    if (!validateForm()) return;
    setConfirmOpen(true);
  };

  const submitPasswordChange = async () => {
    setSubmitError('');
    setLoading(true);
    try {
      const response = await MainService.ChangePassword({
        Password: password,
        CPassword: confirmPassword,
      });
      if (isChangePasswordSuccess(response)) {
        setSuccess('Success');
        setPassword('');
        setConfirmPassword('');
        setConfirmOpen(false);
      } else {
        setSubmitError('Network Error');
        setConfirmOpen(false);
      }
    } catch {
      setSubmitError('Network Error');
      setConfirmOpen(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="mx-auto max-w-lg rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 bg-gray-50 px-6 py-4 text-center rounded-t-xl">
          <div className="flex items-center justify-center gap-2 text-gray-900">
            <Key className="h-6 w-6 text-emerald-600" aria-hidden />
            <h1 className="text-xl font-semibold">Change Password</h1>
          </div>
        </div>

        <div className="p-6">
          {success && (
            <div
              className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900"
              role="status"
            >
              {success}
            </div>
          )}
          {submitError && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
              {submitError}
            </div>
          )}

          <table className="w-full text-sm">
            <tbody className="divide-y divide-gray-100">
              <tr>
                <td className="w-[40%] py-3 align-top font-medium text-gray-700">New Password</td>
                <td className="py-3 align-top">
                  <input
                    type="password"
                    name="Password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setErrorPass(false);
                      setSubmitError('');
                    }}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  />
                  {errorPass && <p className="mt-1 text-xs text-red-600">Enter Password</p>}
                </td>
              </tr>
              <tr>
                <td className="py-3 align-top font-medium text-gray-700">Confirm Password</td>
                <td className="py-3 align-top">
                  <input
                    type="password"
                    name="CPassword"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      setErrorCpass(false);
                      setSubmitError('');
                    }}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  />
                  {errorCpass && (
                    <p className="mt-1 text-xs text-red-600">
                      {!confirmPassword.trim() ? 'Confirm Password' : 'Password not matching'}
                    </p>
                  )}
                </td>
              </tr>
              <tr>
                <td className="py-3" />
                <td className="py-3">
                  <button
                    type="button"
                    onClick={openConfirmModal}
                    disabled={loading}
                    className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-5 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    Change Password
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {confirmOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="change-password-confirm-title"
          onClick={() => !loading && setConfirmOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-xl border border-gray-200 bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-gray-100 px-5 py-4">
              <h3 id="change-password-confirm-title" className="text-lg font-semibold text-gray-900">
                Change password?
              </h3>
              <p className="mt-1 text-sm text-gray-600">
                Your password will be updated for this account. Continue?
              </p>
            </div>
            <div className="flex flex-wrap justify-end gap-2 px-5 py-4 bg-gray-50 rounded-b-xl">
              <button
                type="button"
                disabled={loading}
                onClick={() => setConfirmOpen(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={loading}
                onClick={submitPasswordChange}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-60"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChangePassword;
